import React, { useState, useMemo, useContext, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Grid,
  Card,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
  Scale as ScaleIcon,
  Diamond as DiamondIcon,
  QrCodeScanner as QrCodeScannerIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Summarize as SummarizeIcon
} from '@mui/icons-material';
import { DataContext } from '../../context/DataContext';
import QRScanner from '../../components/QRScanner';

const StockVerification = () => {
  const theme = useTheme();
  const { stock = [] } = useContext(DataContext);

  // State for scanned items
  const [scannedItems, setScannedItems] = useState([]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [verificationComplete, setVerificationComplete] = useState(false);
  
  // State for sold item dialog
  const [soldItemDialogOpen, setSoldItemDialogOpen] = useState(false);
  const [soldItemDetails, setSoldItemDetails] = useState(null);
  
  // State for statistics
  const [statistics, setStatistics] = useState({
    totalScanned: 0,
    totalWeight: 0,
    totalFine: 0,
    categories: {},
    missingItems: [],
    categoryStats: {}
  });
  
  // State for verification session
  const [sessionStarted, setSessionStarted] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  
  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Get all unique categories from stock
  const categories = useMemo(() => {
    const cats = new Set();
    stock.forEach(item => {
      if (item.name) cats.add(item.name);
    });
    return Array.from(cats).sort();
  }, [stock]);

  // Handle QR scan
  const handleQRScan = (qrData) => {
    const scannedQR = qrData.trim();
    
    // Find item in stock
    const stockItem = stock.find(item => item.qr === scannedQR);
    
    if (!stockItem) {
      setSnackbar({
        open: true,
        message: `Item with QR "${scannedQR}" not found in inventory!`,
        severity: 'error'
      });
      return;
    }
    
    // Check if the item is SOLD
    if (stockItem.sold === "Yes") {
      // Show dialog with sold item details
      setSoldItemDetails({
        ...stockItem,
        scannedAt: new Date().toISOString()
      });
      setSoldItemDialogOpen(true);
      return;
    }
    
    // Check if already scanned in this session
    const alreadyScanned = scannedItems.some(item => item.qr === scannedQR);
    
    if (alreadyScanned) {
      setSnackbar({
        open: true,
        message: `Item "${stockItem.name}" (${stockItem.serialNo}) already scanned!`,
        severity: 'warning'
      });
      return;
    }
    
    // Add to scanned items
    const newScannedItem = {
      id: Date.now(),
      ...stockItem,
      scannedAt: new Date().toISOString(),
    };
    
    setScannedItems(prev => [...prev, newScannedItem]);
    
    setSnackbar({
      open: true,
      message: `✓ Scanned: ${stockItem.name} (${stockItem.serialNo}) - Weight: ${stockItem.weight}g`,
      severity: 'success'
    });
    
    // Update statistics
    updateStatistics([...scannedItems, newScannedItem]);
  };
  
  // Update statistics based on scanned items
  const updateStatistics = useCallback((items) => {
    let totalWeight = 0;
    let totalFine = 0;
    const categoriesMap = {};
    const categoryStatsMap = {};
    
    items.forEach(item => {
      const category = item.name || 'Uncategorized';
      const weight = parseFloat(item.weight) || 0;
      const melting = parseFloat(item.melting) || 0;
      const wastage = parseFloat(item.wastage) || 0;
      
      totalWeight += weight;
      totalFine += weight * ((melting + wastage) / 100);
      
      // Category counts
      categoriesMap[category] = (categoriesMap[category] || 0) + 1;
      
      // Category weight & fine stats
      if (!categoryStatsMap[category]) {
        categoryStatsMap[category] = {
          weight: 0,
          fine: 0,
          items: []
        };
      }
      categoryStatsMap[category].weight += weight;
      categoryStatsMap[category].fine += weight * ((melting + wastage) / 100);
      categoryStatsMap[category].items.push(item);
    });
    
    // Find missing items (items in stock but not scanned) - EXCLUDE SOLD ITEMS
    const scannedQrs = new Set(items.map(item => item.qr));
    const missingItems = stock.filter(item => {
      // Only consider unsold items (in stock) that are NOT scanned
      const isUnsold = item.sold !== "Yes";
      const notScanned = !scannedQrs.has(item.qr);
      return isUnsold && notScanned;
    });
    
    setStatistics({
      totalScanned: items.length,
      totalWeight: totalWeight.toFixed(3),
      totalFine: totalFine.toFixed(3),
      categories: categoriesMap,
      categoryStats: categoryStatsMap,
      missingItems: missingItems,
      missingCount: missingItems.length,
      missingWeight: missingItems.reduce((sum, item) => sum + (parseFloat(item.weight) || 0), 0).toFixed(3)
    });
  }, [stock]);
  
  // Start verification session
  const startVerification = () => {
    setScannedItems([]);
    setVerificationComplete(false);
    setSessionStarted(true);
    setSessionStartTime(new Date());
    setSearchTerm('');
    setSelectedCategory('all');
    setStatistics({
      totalScanned: 0,
      totalWeight: 0,
      totalFine: 0,
      categories: {},
      categoryStats: {},
      missingItems: [],
      missingCount: 0,
      missingWeight: 0
    });
    setSnackbar({
      open: true,
      message: 'Stock verification session started! Start scanning items.',
      severity: 'success'
    });
  };
  
  // Complete verification
  const completeVerification = () => {
    setVerificationComplete(true);
    setSessionStarted(false);
    setScannerOpen(false);
    setSnackbar({
      open: true,
      message: `Verification complete! Scanned ${statistics.totalScanned} items. Missing ${statistics.missingCount} items.`,
      severity: 'info'
    });
  };
  
  // Reset verification
  const resetVerification = () => {
    setScannedItems([]);
    setVerificationComplete(false);
    setSessionStarted(false);
    setSessionStartTime(null);
    setSearchTerm('');
    setSelectedCategory('all');
    setStatistics({
      totalScanned: 0,
      totalWeight: 0,
      totalFine: 0,
      categories: {},
      categoryStats: {},
      missingItems: [],
      missingCount: 0,
      missingWeight: 0
    });
  };
  
  // Remove scanned item
  const removeScannedItem = (itemId) => {
    const updatedItems = scannedItems.filter(item => item.id !== itemId);
    setScannedItems(updatedItems);
    updateStatistics(updatedItems);
    setSnackbar({
      open: true,
      message: 'Item removed from scanned list',
      severity: 'info'
    });
  };
  
  // Export report
  const handleExport = () => {
    if (scannedItems.length === 0 && !verificationComplete) {
      setSnackbar({
        open: true,
        message: 'No data to export',
        severity: 'warning'
      });
      return;
    }
    
    const reportData = {
      sessionStartTime: sessionStartTime,
      sessionEndTime: new Date().toISOString(),
      totalScanned: statistics.totalScanned,
      totalWeight: statistics.totalWeight,
      totalFine: statistics.totalFine,
      missingCount: statistics.missingCount,
      missingWeight: statistics.missingWeight,
      scannedItems: scannedItems.map(item => ({
        serialNo: item.serialNo,
        name: item.name,
        metal: item.metal,
        weight: item.weight,
        huid: item.huid,
        scannedAt: item.scannedAt
      })),
      missingItems: statistics.missingItems.map(item => ({
        serialNo: item.serialNo,
        name: item.name,
        metal: item.metal,
        weight: item.weight,
        huid: item.huid
      })),
      categoryBreakdown: statistics.categoryStats
    };
    
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `stock_verification_${new Date().toISOString().split('T')[0]}.json`);
    link.click();
    URL.revokeObjectURL(url);
    
    setSnackbar({
      open: true,
      message: 'Report exported successfully',
      severity: 'success'
    });
  };
  
  // Print report
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const totalStockItems = stock.filter(item => item.sold !== "Yes").length;
    const verificationRate = totalStockItems > 0 ? ((statistics.totalScanned / totalStockItems) * 100).toFixed(1) : 0;
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Stock Verification Report</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 40px;
            line-height: 1.6;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
          }
          .summary {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 15px;
            margin-bottom: 30px;
          }
          .summary-card {
            border: 1px solid #ddd;
            border-radius: 8px;
            padding: 15px;
            background: #f9f9f9;
          }
          .summary-card h3 {
            margin: 0 0 10px 0;
            color: #333;
          }
          .summary-card .value {
            font-size: 24px;
            font-weight: bold;
            color: #2563EB;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 10px;
            text-align: left;
          }
          th {
            background: #f0f0f0;
            font-weight: bold;
          }
          .missing {
            background: #fee2e2;
          }
          .footer {
            text-align: center;
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            font-size: 12px;
            color: #666;
          }
          h2 {
            color: #2563EB;
            margin-top: 30px;
          }
          .badge {
            display: inline-block;
            padding: 3px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: bold;
          }
          .badge-success {
            background: #10b981;
            color: white;
          }
          .badge-warning {
            background: #f59e0b;
            color: white;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Stock Verification Report</h1>
          <p>Generated on: ${new Date().toLocaleString()}</p>
          <p>Session: ${sessionStartTime ? new Date(sessionStartTime).toLocaleString() : 'N/A'} - ${new Date().toLocaleString()}</p>
        </div>
        
        <div class="summary">
          <div class="summary-card">
            <h3>📊 Verification Rate</h3>
            <div class="value">${verificationRate}%</div>
            <small>${statistics.totalScanned} of ${totalStockItems} items scanned</small>
          </div>
          <div class="summary-card">
            <h3>✅ Scanned Items</h3>
            <div class="value">${statistics.totalScanned}</div>
          </div>
          <div class="summary-card">
            <h3>⚠️ Missing Items</h3>
            <div class="value">${statistics.missingCount}</div>
          </div>
          <div class="summary-card">
            <h3>⚖️ Total Weight Scanned</h3>
            <div class="value">${statistics.totalWeight} gm</div>
          </div>
          <div class="summary-card">
            <h3>💎 Total Fine Scanned</h3>
            <div class="value">${statistics.totalFine} gm</div>
          </div>
          <div class="summary-card">
            <h3>📦 Missing Weight</h3>
            <div class="value">${statistics.missingWeight} gm</div>
          </div>
        </div>
        
        <h2>📋 Scanned Items (${scannedItems.length})</h2>
        <table>
          <thead>
            <tr><th>Serial No</th><th>Item Name</th><th>Metal</th><th>Weight (gm)</th><th>HUID</th><th>Scanned At</th></tr>
          </thead>
          <tbody>
            ${scannedItems.map(item => `
              <tr>
                <td>${item.serialNo || '-'}</td>
                <td>${item.name || '-'}</td>
                <td>${item.metal || '-'}</td>
                <td>${item.weight || 0}</td>
                <td>${item.huid || '-'}</td>
                <td>${new Date(item.scannedAt).toLocaleTimeString()}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <h2>⚠️ Missing Items (${statistics.missingItems.length})</h2>
        <table>
          <thead>
            <tr><th>Serial No</th><th>Item Name</th><th>Metal</th><th>Weight (gm)</th><th>HUID</th></tr>
          </thead>
          <tbody>
            ${statistics.missingItems.map(item => `
              <tr class="missing">
                <td>${item.serialNo || '-'}</td>
                <td>${item.name || '-'}</td>
                <td>${item.metal || '-'}</td>
                <td>${item.weight || 0}</td>
                <td>${item.huid || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          <p>This is a system-generated report. Please verify manually if any discrepancies exist.</p>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };
  
  // Filtered scanned items for display
  const filteredScannedItems = useMemo(() => {
    let filtered = scannedItems;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(term) ||
        item.serialNo?.toString().includes(term) ||
        item.huid?.toLowerCase().includes(term)
      );
    }
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.name === selectedCategory);
    }
    
    return filtered;
  }, [scannedItems, searchTerm, selectedCategory]);
  
  // Calculate category-wise totals for display
  const categoryTotals = useMemo(() => {
    const totals = {};
    scannedItems.forEach(item => {
      const category = item.name || 'Uncategorized';
      if (!totals[category]) {
        totals[category] = { count: 0, weight: 0, fine: 0 };
      }
      totals[category].count++;
      totals[category].weight += parseFloat(item.weight) || 0;
      const melting = parseFloat(item.melting) || 0;
      const wastage = parseFloat(item.wastage) || 0;
      totals[category].fine += (parseFloat(item.weight) || 0) * ((melting + wastage) / 100);
    });
    return totals;
  }, [scannedItems]);
  
  // Sold Item Dialog Component
  const SoldItemDialog = () => (
    <Dialog
      open={soldItemDialogOpen}
      onClose={() => setSoldItemDialogOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        //   background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.05)}, ${alpha(theme.palette.warning.main, 0.05)})`
        }
      }}
    >
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <WarningIcon />
        <Typography variant="h6" fontWeight={700}>
          Item Already Sold!
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ mt: 2 }}>
        {soldItemDetails && (
          <Box>
            {/* Warning Banner */}
            <Box sx={{
              p: 2,
              mb: 3,
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <WarningIcon sx={{ color: theme.palette.warning.main }} />
              <Typography variant="body2" color="warning.main">
                This item has been marked as SOLD and cannot be verified in the current stock.
              </Typography>
            </Box>
            
            {/* Item Details */}
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Item Information
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">Serial Number</Typography>
                  <Typography variant="body1" fontWeight={700}>{soldItemDetails.serialNo || 'N/A'}</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">Item Name</Typography>
                  <Typography variant="body1" fontWeight={700}>{soldItemDetails.name || 'N/A'}</Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">Metal</Typography>
                  <Chip 
                    label={soldItemDetails.metal || 'N/A'} 
                    size="small"
                    sx={{
                        ml: 1.5,
                      mt: 0.5,
                      bgcolor: soldItemDetails.metal === 'Gold' ? alpha('#f59e0b', 0.2) : alpha('#94a3b8', 0.2),
                      color: soldItemDetails.metal === 'Gold' ? '#d97706' : '#64748b',
                      fontWeight: 600
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">Weight</Typography>
                  <Typography variant="body1" fontWeight={700}>{parseFloat(soldItemDetails.weight).toFixed(3)} gm</Typography>
                </Box>
              </Grid>
              {soldItemDetails.huid && (
                <Grid item xs={12}>
                  <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">HUID</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{soldItemDetails.huid}</Typography>
                  </Box>
                </Grid>
              )}
              {soldItemDetails.supplierName && (
                <Grid item xs={12}>
                  <Box sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">Supplier</Typography>
                    <Typography variant="body2">{soldItemDetails.supplierName}</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Verification Status
            </Typography>
            
            <Box sx={{
              p: 2,
              bgcolor: alpha(theme.palette.error.main, 0.1),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CancelIcon sx={{ color: theme.palette.error.main }} />
                <Typography variant="body2" fontWeight={600} color="error.main">
                  This item is excluded from verification
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Sold items are automatically excluded from stock verification. 
                Please verify only unsold items in your physical stock.
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button
          onClick={() => setSoldItemDialogOpen(false)}
          variant="contained"
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            borderRadius: 2,
            px: 3
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
  
  return (
    <Box sx={{ minHeight: '100vh', py: 4, bgcolor: 'background.default' }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}
          >
            Stock Verification System
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Scan all physical items to verify against digital inventory
          </Typography>
        </Box>
        
        {/* Control Panel */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {!sessionStarted && !verificationComplete && (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<QrCodeScannerIcon />}
                    onClick={startVerification}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      fontWeight: 700,
                      '&:hover': { transform: 'translateY(-2px)' }
                    }}
                  >
                    Start Verification
                  </Button>
                )}
                
                {sessionStarted && (
                  <>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<QrCodeScannerIcon />}
                      onClick={() => setScannerOpen(true)}
                      sx={{ borderRadius: 2, px: 4, py: 1.5, fontWeight: 700 }}
                    >
                      Scan Item
                    </Button>
                    
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<CheckCircleIcon />}
                      onClick={completeVerification}
                      sx={{ borderRadius: 2, px: 4, py: 1.5, fontWeight: 700 }}
                    >
                      Complete Verification
                    </Button>
                    
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<RefreshIcon />}
                      onClick={resetVerification}
                      color="error"
                      sx={{ borderRadius: 2, px: 4, py: 1.5 }}
                    >
                      Reset Session
                    </Button>
                  </>
                )}
                
                {verificationComplete && (
                  <>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<PrintIcon />}
                      onClick={handlePrint}
                      sx={{ borderRadius: 2, px: 4, py: 1.5 }}
                    >
                      Print Report
                    </Button>
                    
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<DownloadIcon />}
                      onClick={handleExport}
                      sx={{ borderRadius: 2, px: 4, py: 1.5 }}
                    >
                      Export Report
                    </Button>
                    
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<RefreshIcon />}
                      onClick={resetVerification}
                      sx={{ borderRadius: 2, px: 4, py: 1.5 }}
                    >
                      New Verification
                    </Button>
                  </>
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              {(sessionStarted || verificationComplete) && scannedItems.length > 0 && (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
                  <TextField
                    size="small"
                    placeholder="Search scanned items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                    }}
                    sx={{ width: 250 }}
                  />
                  
                  <TextField
                    select
                    size="small"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    sx={{ width: 200 }}
                    SelectProps={{ native: true }}
                  >
                    <option value="all">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </TextField>
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>
        
        {/* Statistics Cards */}
        {(sessionStarted || verificationComplete) && (
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} sm={3}>
              <Card sx={{ p: 2, borderRadius: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.dark, 0.05)})` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">SCANNED ITEMS</Typography>
                    <Typography variant="h4" fontWeight={700} color="success.main">{statistics.totalScanned}</Typography>
                  </Box>
                  <CheckCircleIcon sx={{ fontSize: 40, color: theme.palette.success.main, opacity: 0.7 }} />
                </Box>
              </Card>
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <Card sx={{ p: 2, borderRadius: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)}, ${alpha(theme.palette.warning.dark, 0.05)})` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">MISSING ITEMS</Typography>
                    <Typography variant="h4" fontWeight={700} color="warning.main">{statistics.missingCount}</Typography>
                  </Box>
                  <WarningIcon sx={{ fontSize: 40, color: theme.palette.warning.main, opacity: 0.7 }} />
                </Box>
              </Card>
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <Card sx={{ p: 2, borderRadius: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.dark, 0.05)})` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">TOTAL WEIGHT</Typography>
                    <Typography variant="h4" fontWeight={700} color="info.main">{statistics.totalWeight}gm</Typography>
                  </Box>
                  <ScaleIcon sx={{ fontSize: 40, color: theme.palette.info.main, opacity: 0.7 }} />
                </Box>
              </Card>
            </Grid>
            
            <Grid item xs={6} sm={3}>
              <Card sx={{ p: 2, borderRadius: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)}, ${alpha(theme.palette.secondary.dark, 0.05)})` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">TOTAL FINE</Typography>
                    <Typography variant="h4" fontWeight={700} color="secondary.main">{statistics.totalFine}gm</Typography>
                  </Box>
                  <DiamondIcon sx={{ fontSize: 40, color: theme.palette.secondary.main, opacity: 0.7 }} />
                </Box>
              </Card>
            </Grid>
          </Grid>
        )}
        
        {/* Main Content - Scanned Items Table */}
        {(sessionStarted || verificationComplete) && (
          <Grid container spacing={3}>
            {/* Left Column - Scanned Items */}
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" fontWeight={700}>
                    <InventoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Scanned Items ({filteredScannedItems.length})
                  </Typography>
                  <Chip
                    label={`${((statistics.totalScanned / (stock.filter(i => i.sold !== "Yes").length)) * 100).toFixed(1)}% Complete`}
                    color="primary"
                    size="small"
                  />
                </Box>
                
                {filteredScannedItems.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <QrCodeScannerIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                    <Typography color="text.secondary">
                      {sessionStarted ? 'Start scanning items to see them here' : 'No items scanned yet'}
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                          <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Serial No</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Item Name</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Metal</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Weight (gm)</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>HUID</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredScannedItems.map((item, index) => (
                          <TableRow key={item.id} hover>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{item.serialNo || '-'}</TableCell>
                            <TableCell>{item.name || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={item.metal || '-'}
                                size="small"
                                sx={{
                                  bgcolor: item.metal === 'Gold' ? alpha('#f59e0b', 0.1) : alpha('#94a3b8', 0.1),
                                  color: item.metal === 'Gold' ? '#d97706' : '#64748b',
                                  fontWeight: 600
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{parseFloat(item.weight).toFixed(3)}</TableCell>
                            <TableCell>
                              {item.huid && (
                                <Chip
                                  label={item.huid}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontFamily: 'monospace', fontSize: '11px' }}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeScannedItem(item.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
            
            {/* Right Column - Summary & Missing Items */}
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                  <SummarizeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Category Summary
                </Typography>
                
                {Object.keys(categoryTotals).length === 0 ? (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No items scanned yet
                  </Typography>
                ) : (
                  Object.entries(categoryTotals).map(([category, data]) => (
                    <Box key={category} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>{category}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {data.count} items • {data.weight.toFixed(2)} gm
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(data.count / statistics.totalScanned) * 100}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  ))
                )}
              </Paper>
              
              <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: 'warning.main' }}>
                  <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Missing Items ({statistics.missingCount})
                </Typography>
                
                {statistics.missingCount === 0 ? (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No missing items! All inventory accounted for.
                  </Typography>
                ) : (
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {statistics.missingItems.map((item, index) => (
                      <Card key={item.id} sx={{ p: 1.5, mb: 1, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>{item.serialNo || 'N/A'}</Typography>
                            <Typography variant="caption" color="text.secondary">{item.name} • {item.metal}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" fontWeight={600}>{parseFloat(item.weight).toFixed(3)} gm</Typography>
                            {item.huid && <Typography variant="caption" color="text.secondary">{item.huid}</Typography>}
                          </Box>
                        </Box>
                      </Card>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
        
        {/* QR Scanner Modal */}
        <QRScanner
          open={scannerOpen}
          onClose={() => setScannerOpen(false)}
          onScan={handleQRScan}
        />
        
        {/* Sold Item Dialog */}
        <SoldItemDialog />
        
        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default StockVerification;